import React, { useState } from 'react'
import { Alert, Grid, styled, Typography } from '@mui/material'
import { faMessage } from '@fortawesome/pro-light-svg-icons/faMessage'
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons/faPaperPlane'

import { Button } from '@/components/Button/Button'
import { Icon } from '@/components/Icon/Icon'
import { TextField } from '@/components/TextField/TextField'
import { EMAILS } from '@/shared/emails'
import { pxToRem } from '@/shared/styles/themes'

import { useContactCreate } from '@/shared/api/generated/contact/contact'

type ContactFormData = {
  name: string
  email: string
  message: string
}

export const ContactSlice: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    message: '',
  })

  const { mutate, isPending, isSuccess, isError, reset } = useContactCreate()

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    mutate(
      { data: formData },
      {
        onSuccess: () => {
          setFormData({ name: '', email: '', message: '' })
          setTimeout(() => reset(), 4000)
        },
        onError: () => {
          setTimeout(() => reset(), 4000)
        },
      },
    )
  }

  return (
    <StyledWrapper>
      <StyledHeaderTypography variant="h1">Contact Us</StyledHeaderTypography>

      <StyledContentGrid container spacing={2}>
        <Grid item xs={12} md={5}>
          <StyledStorySection>
            <StyledContactText variant="body1">
              Your insights shape our journey. Whether it's a feature suggestion, bug report, or just saying hello—we'd love to hear from you. Every message helps us build a better platform for our growing community of collectors.
            </StyledContactText>
            <StyledEmailButtonContainer>
              <StyledCtaButton
                href={`mailto:${EMAILS.CONTACT}`}
                primary
                startIcon={<Icon icon={faPaperPlane} />}
              >
              Email Us Directly
              </StyledCtaButton>
            </StyledEmailButtonContainer>
          </StyledStorySection>
        </Grid>

        <Grid item xs={12} md={7}>
          <StyledFormSection>
            <StyledSectionSubtitle variant="h6">Send us a message</StyledSectionSubtitle>

            { isSuccess && (
              <Alert severity="success" sx={{ mb: 3 }}>
                Thank you for your message! We'll get back to you within 48 hours.
              </Alert>
            )}

            { isError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                There was an error sending your message. Please try again.
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <StyledTopField>
                <StyledFormField>
                  <StyledTextField
                    name="name"
                    label="Your Name"
                    fullWidth
                    required
                    value={formData.name}
                    onChange={handleChange}
                  />
                </StyledFormField>

                <StyledFormField>
                  <StyledTextField
                    name="email"
                    label="Email Address"
                    type="email"
                    fullWidth
                    required
                    value={formData.email}
                    onChange={handleChange}
                  />
                </StyledFormField>
              </StyledTopField>

              <StyledFormField>
                <StyledTextField
                  name="message"
                  label="Message"
                  multiline
                  rows={5}
                  fullWidth
                  required
                  value={formData.message}
                  onChange={handleChange}
                />
              </StyledFormField>

              <StyledButtonContainer>
                <StyledCtaButton
                  primary
                  type="submit"
                  disabled={isPending}
                  startIcon={<Icon icon={faMessage} />}
                >
                  {isPending ? 'Sending...' : 'Send Message'}
                </StyledCtaButton>
              </StyledButtonContainer>
            </form>
          </StyledFormSection>
        </Grid>
      </StyledContentGrid>
    </StyledWrapper>
  )
}

const StyledCtaButton = styled(Button)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(2, 4),
  color: theme.palette.secondary.main,

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2.5, 5),
  },
}))

const StyledContactText =  styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginBottom: pxToRem(15),
}))

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& input, & textarea': {
    color: theme.palette.primary.main,
  },
  '& label': {
    color: theme.palette.primary.main,
  },
}))

const StyledWrapper = styled('div')`
    width: 100%;
`

const StyledContentGrid = styled(Grid)`
    margin-top: ${pxToRem(8)};
`

const StyledHeaderTypography = styled(Typography)`
    font-size: ${pxToRem(36)};
    margin-top: ${pxToRem(20)}; 
    color: ${props => props.theme.palette.primary.main};
`

const StyledSectionSubtitle = styled(Typography)`
    font-size: ${pxToRem(21)};
    margin-bottom: ${pxToRem(16)};
    font-weight: 400;
    color: ${props => props.theme.palette.primary.main};
`
const StyledStorySection = styled('div')`
    height: 100%;
    display: flex;
    flex-direction: column;
`
const StyledEmailButtonContainer = styled('div')`
  display: flex;
  justify-content: flex-start;
  width: 100%;
`
const StyledFormField = styled('div')`
  margin-bottom: ${pxToRem(16)};
`

const StyledTopField = styled('div')`
    display: flex;
    flex-direction: column;
    gap: ${pxToRem(16)};
`
const StyledButtonContainer = styled('div')`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`

const StyledFormSection = styled('div')`
  padding: ${pxToRem(24)};
  background-color: ${props => props.theme.palette.background.paper};
  border-radius: ${pxToRem(12)};
  border: 1px solid ${props => props.theme.palette.divider};
  width: 100%;
  box-sizing: border-box;
  margin-bottom: ${pxToRem(25)};; 
    
  ${props => props.theme.breakpoints.up('md')} {
    margin-bottom: ${pxToRem(25)};; 
  }
`